import cn from 'classnames';
import React from 'react';

import { Coords, DesignDimensions } from 'editor/src/store/design/types';
import { Unit } from 'editor/src/store/editor/types';

import { convertMmToUnit } from 'editor/src/util/math';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { GuideAxis } from 'editor/src/component/EditorArea/RulerGuides/geometry';

import styles from './index.module.scss';

interface Props {
  spreadWidth: number;
  spreadHeight: number;
  dimensions: Pick<DesignDimensions, 'width' | 'height'>;
  spreadCoords: Coords;
  xDelimiters: number[];
  yDelimiters: number[];
  zoom: number;
  unit: Unit;
  offsetX: number;
  onGuideStart?: (event: React.PointerEvent, axis: GuideAxis) => void;
  reverseX?: boolean;
  reverseY?: boolean;
}

function RulersCss({
  spreadCoords,
  dimensions,
  spreadWidth,
  spreadHeight,
  xDelimiters,
  yDelimiters,
  zoom,
  unit,
  offsetX,
  onGuideStart,
  reverseX,
  reverseY,
}: Props) {
  const fabricCanvas = useFabricCanvas();
  const {
    tl: { x: left, y: top },
  } = fabricCanvas.calcViewportBoundaries();
  const { mm2px } = useFabricUtils();

  return (
    <>
      <div
        className={styles.axisHorizontal}
        data-ruler="horizontal"
        style={{ pointerEvents: 'auto', cursor: onGuideStart ? 'ns-resize' : undefined }}
        onPointerDown={(event) => onGuideStart?.(event, 'horizontal')}
      >
        <div
          className={styles.axisHorizontalSpread}
          style={{
            left: (spreadCoords.left - left) * zoom - 1.5,
            width: mm2px(spreadWidth * zoom) + 1,
          }}
        >
          {xDelimiters.map((delimiter, index) => (
            <div
              key={index}
              className={cn(styles.delimiterNumber, styles.delimiterNumberHorizontal, {
                [styles.spreadSize]: delimiter + offsetX === 0,
              })}
              style={{
                left: mm2px((reverseX ? spreadWidth - delimiter - offsetX : delimiter + offsetX) * zoom),
                opacity: mm2px(Math.abs(delimiter + offsetX - spreadWidth) * zoom) < 30 ? 0 : 1,
              }}
            >
              {+convertMmToUnit(delimiter, unit).toFixed(1)}
              <div className={styles.delimiter} />
            </div>
          ))}
          <div
            className={cn(styles.delimiterNumber, styles.delimiterNumberHorizontal, styles.spreadSize)}
            style={{ left: reverseX ? 0 : mm2px(spreadWidth * zoom) }}
          >
            {+convertMmToUnit(dimensions.width - offsetX, unit).toFixed(1)}
          </div>
        </div>
      </div>
      <div
        className={styles.axisVertical}
        data-ruler="vertical"
        style={{ pointerEvents: 'auto', cursor: onGuideStart ? 'ew-resize' : undefined }}
        onPointerDown={(event) => onGuideStart?.(event, 'vertical')}
      >
        <div
          className={styles.axisVerticalSpread}
          style={{
            top: (spreadCoords.top - top) * zoom - 1,
            height: mm2px(spreadHeight * zoom),
          }}
        >
          {yDelimiters.map((delimiter, index) => (
            <div
              key={index}
              className={cn(styles.delimiterNumber, styles.delimiterNumberVertical, {
                [styles.spreadSize]: index === 0,
              })}
              style={{
                top: mm2px((reverseY ? spreadHeight - delimiter : delimiter) * zoom),
                opacity: mm2px(Math.abs(delimiter - dimensions.height) * zoom) < 25 ? 0 : 1,
              }}
            >
              {+convertMmToUnit(delimiter, unit).toFixed(1)}
              <div className={styles.delimiter} />
            </div>
          ))}
          <span
            className={cn(styles.delimiterNumber, styles.delimiterNumberVertical, styles.spreadSize)}
            style={{ top: reverseY ? 0 : mm2px(spreadHeight * zoom) }}
          >
            {+convertMmToUnit(dimensions.height, unit).toFixed(1)}
          </span>
        </div>
      </div>
      <div className={styles.crossing} />
    </>
  );
}

export default React.memo(RulersCss);
